import { fetchApiGetV2, fetchApiPutV2 } from "../../../../api/api";
import { updateLanguageSettings } from "./languageSettingsActions";
import {
  languageCodeFromActiveLanguage,
  visitorStateFromLanguage,
} from "../../../../components/_default/utilities/languageCode";

export interface Language {
  /** Omitted in some V2 `/languageSettings` payloads — use `languageCode` + array index if needed. */
  id?: number;
  /** V2 flat row (e.g. `et`, `en`). */
  languageCode?: string;
  locale?: string;
  languageShortname?: string;
  manager_state?: number;
  visitor_state?: number;
  visitorState?: number;
  /** Display name (V2 top-level). */
  name?: string;
  icon?: string;
  order?: number;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  website_id?: number;
  /** Legacy nested language; V2 may omit. */
  language?: {
    id?: number;
    name?: string;
    shortname?: string;
    icon?: string;
    icon_url?: string;
  } | null;
}

export interface LanguageSetting {
  useBrowserLanguage: boolean;
  activeLanguages: Array<Language>;
}

function trimStr(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/**
 * API rows sometimes omit `languageCode` but send `languageShortname`.
 * V2 returns camelCase `visitorState`; UI historically reads `visitor_state`.
 * Normalize once at ingest so selects and filters stay consistent.
 */
export function normalizeLanguageSettingPayload(
  payload: LanguageSetting
): LanguageSetting {
  const activeLanguages = (payload.activeLanguages ?? []).map((lang) => {
    const code =
      trimStr(lang.languageCode) ||
      trimStr(lang.locale) ||
      trimStr(lang.languageShortname) ||
      trimStr(lang.language?.shortname);
    const visitor = visitorStateFromLanguage(lang);
    return {
      ...lang,
      ...(code ? { languageCode: code } : {}),
      visitor_state: visitor,
      visitorState: visitor,
    };
  });
  return {
    ...payload,
    useBrowserLanguage: Boolean(payload.useBrowserLanguage),
    activeLanguages,
  };
}

/** V2 PUT rejects `id`; send languageCode + visitorState only. */
export function toLanguageSettingsV2SavePayload(data: LanguageSetting): {
  useBrowserLanguage: boolean;
  activeLanguages: Array<{
    languageCode: string;
    order: number;
    visitorState: number;
  }>;
} {
  return {
    useBrowserLanguage: Boolean(data.useBrowserLanguage),
    activeLanguages: (data.activeLanguages ?? []).map((lang, index) => {
      const languageCode = languageCodeFromActiveLanguage(lang);
      if (!languageCode) {
        throw new Error(
          `activeLanguages[${index}] is missing languageCode for save`
        );
      }
      return {
        languageCode,
        order: Number(lang.order ?? index + 1),
        visitorState: visitorStateFromLanguage(lang),
      };
    }),
  };
}

const LanguageSettingsAPI = {
  saveLanguageSettings,
  loadLanguageSettings,
};
export default LanguageSettingsAPI;

export function loadLanguageSettings() {
  return fetchApiGetV2<LanguageSetting>(`/languageSettings`, {}).then((res) => {
    updateLanguageSettings(res);
    return res;
  });
}

export function saveLanguageSettings(data: LanguageSetting) {
  const payload = toLanguageSettingsV2SavePayload(data);
  return fetchApiPutV2<LanguageSetting>(`/languageSettings`, payload).then(
    (res) => {
      updateLanguageSettings(res ?? data);
      return res;
    }
  );
}
